import { FlexCol, FlexRow } from '@components/layout/Flex';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchFileBlob } from '@data/farseerFile/farseerFileActions';
import { parseCsvText } from './parseCsvText';
import { topLevelColumnNames } from './topLevelColumnNames';
import { formatWarningScope, PipelineStep } from './parsePipelineLogs';
import {
    DetailContainer,
    DetailHeader,
    DetailLabel,
    DetailSection,
    DetailValue,
    DataTable,
    DataTh,
    DataTd,
    ErrorMessageBox,
    RowNumberTh,
    RowNumberTd,
    ShowMoreButton,
    StepDescription,
    StepTypeBadge,
    CloseButton,
    DataTableWrapper,
    NoDataMessage,
    SkippedMessageBox,
    WarningEntry,
    WarningRuleTag,
    WarningSection,
    WarningSectionHeading,
} from './StepDetailPanel.style';

const ROWS_PER_PAGE = 15;

interface StepDetailPanelProps {
    step: PipelineStep;
    fileReference?: string;
    fileName?: string;
    signedUrl?: string;
    isLatestRun?: boolean;
    hasNonPreviewableFile?: boolean;
    onClose: () => void;
}

function StepTypeLabel({ step }: { step: PipelineStep }) {
    if (step.stepType === 'extraction') return <>Extraction</>;
    if (step.stepType === 'transform') return <>Transform</>;
    if (step.stepType === 'load') return <>Load</>;
    if (step.stepType === 'export') return <>Export</>;
    return <>Step</>;
}

function formatSourceInfo(sourceInfo: string): string {
    if (sourceInfo.startsWith('sharepoint ')) return `SharePoint: ${sourceInfo.slice('sharepoint '.length)}`;
    if (sourceInfo.startsWith('google_drive ')) return `Google Drive: ${sourceInfo.slice('google_drive '.length)}`;
    return sourceInfo;
}

function StatusLabel({ step }: { step: PipelineStep }) {
    const labels: Record<string, string> = {
        PENDING: 'Pending',
        RUNNING: 'Running...',
        OK: 'Completed',
        EXTRACTED: 'Extracted',
        LOADED: 'Loaded',
        EXPORTED: 'Exported',
        SKIPPED: 'Skipped',
        FAILED: 'Failed',
    };
    return <>{labels[step.status] ?? step.status}</>;
}

async function parseParquetData(blob: Blob): Promise<string[][]> {
    const { parquetRead, parquetMetadataAsync } = await import('hyparquet');
    const buffer = await blob.arrayBuffer();

    const metadata = await parquetMetadataAsync(buffer);
    const columnNames = topLevelColumnNames(metadata.schema);

    const rows = await new Promise<unknown[][]>((resolve) => {
        parquetRead({ file: buffer, rowEnd: 200, onComplete: resolve });
    });

    return [columnNames, ...rows.map((row) => row.map((val) => (val == null ? '' : String(val))))];
}

function DataPreview({
    fileReference,
    fileName,
    signedUrl,
}: {
    fileReference: string;
    fileName: string;
    signedUrl?: string;
}) {
    const isParquet = fileName.endsWith('.parquet');
    const {
        data: parsed,
        isInitialLoading,
        error,
    } = useQuery(
        ['pipeline-file-preview', fileReference, signedUrl],
        async () => {
            const blob = await fetchFileBlob(fileReference, signedUrl);
            if (isParquet) return parseParquetData(blob);
            const text = await blob.text();
            return parseCsvText(text);
        },
        { keepPreviousData: true }
    );
    const [visibleRows, setVisibleRows] = useState(ROWS_PER_PAGE);

    if (isInitialLoading) {
        return <NoDataMessage>Loading data preview...</NoDataMessage>;
    }

    if (error || !parsed || parsed.length === 0) {
        return <NoDataMessage>Could not load data preview</NoDataMessage>;
    }

    const headers = parsed[0];
    const rows = parsed.slice(1);
    const totalRows = rows.length;
    const displayedRows = rows.slice(0, visibleRows);
    const hasMore = visibleRows < totalRows;

    return (
        <FlexCol $gap={8}>
            <FlexRow $align="center" $spaceBetween>
                <DetailLabel style={{ fontWeight: 600 }}>
                    Data Preview — {fileName} ({totalRows} rows)
                </DetailLabel>
            </FlexRow>
            <DataTableWrapper>
                <DataTable>
                    <thead>
                        <tr>
                            <RowNumberTh>#</RowNumberTh>
                            {headers.map((h, i) => (
                                <DataTh key={i}>{h}</DataTh>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {displayedRows.map((row, ri) => (
                            <tr key={ri}>
                                <RowNumberTd>{ri + 1}</RowNumberTd>
                                {headers.map((_, ci) => (
                                    <DataTd key={ci}>{row[ci] ?? ''}</DataTd>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </DataTable>
            </DataTableWrapper>
            {hasMore && (
                <ShowMoreButton onClick={() => setVisibleRows((v) => v + ROWS_PER_PAGE)}>
                    Show more ({totalRows - visibleRows} remaining)
                </ShowMoreButton>
            )}
        </FlexCol>
    );
}

export function StepDetailPanel({
    step,
    fileReference,
    fileName,
    signedUrl,
    isLatestRun = true,
    hasNonPreviewableFile = false,
    onClose,
}: StepDetailPanelProps) {
    // Export steps are external sinks — they push bytes to SharePoint /
    // SFTP / webhook / ..., not to a Farseer data store.  There's no
    // fileReference the runner could register, so showing the preview
    // section (which then falls through to "No data file found") looks
    // like a failure even though the export succeeded.  The header,
    // status, and row count carry the success signal on their own.
    //
    // LOADED has the same underlying issue — a load step writes into
    // RAMA, not the data store, so no file reference exists for it
    // either — but the preview fallback was pre-existing behaviour
    // when the load step shipped, and changing it is out of scope for
    // this PR.  Left here as a known wart for a follow-up.
    const hasData = step.status === 'OK' || step.status === 'EXTRACTED' || step.status === 'LOADED';

    return (
        <DetailContainer>
            <DetailHeader>
                <FlexRow $align="center" $gap={8}>
                    <span style={{ fontWeight: 700, fontSize: 14 }}>{step.name}</span>
                    <StepTypeBadge>
                        <StepTypeLabel step={step} />
                    </StepTypeBadge>
                </FlexRow>
                <CloseButton onClick={onClose} aria-label="Close step details">
                    &times;
                </CloseButton>
            </DetailHeader>

            {step.description && <StepDescription>{step.description}</StepDescription>}

            <FlexRow $gap={24} style={{ padding: '8px 16px' }}>
                <FlexCol $gap={2}>
                    <DetailLabel>Status</DetailLabel>
                    <DetailValue>
                        <StatusLabel step={step} />
                    </DetailValue>
                </FlexCol>
                {step.elapsedSeconds !== undefined && (
                    <FlexCol $gap={2}>
                        <DetailLabel>Duration</DetailLabel>
                        <DetailValue>{step.elapsedSeconds.toFixed(1)}s</DetailValue>
                    </FlexCol>
                )}
                {step.rowCount !== undefined && (
                    <FlexCol $gap={2}>
                        <DetailLabel>Rows</DetailLabel>
                        <DetailValue>{step.rowCount.toLocaleString()}</DetailValue>
                    </FlexCol>
                )}
                <FlexCol $gap={2}>
                    <DetailLabel>Position</DetailLabel>
                    <DetailValue>
                        Step {step.index} of {step.total}
                    </DetailValue>
                </FlexCol>
                {step.deps.length > 0 && (
                    <FlexCol $gap={2}>
                        <DetailLabel>Depends on</DetailLabel>
                        <DetailValue>{step.deps.join(', ')}</DetailValue>
                    </FlexCol>
                )}
                {step.sourceInfo && (
                    <FlexCol $gap={2}>
                        <DetailLabel>Source</DetailLabel>
                        <DetailValue>{formatSourceInfo(step.sourceInfo)}</DetailValue>
                    </FlexCol>
                )}
            </FlexRow>

            {step.errorMessage && <ErrorMessageBox>{step.errorMessage}</ErrorMessageBox>}
            {step.skipReason && <SkippedMessageBox>{step.skipReason}</SkippedMessageBox>}

            {step.warnings && step.warnings.length > 0 && (
                <WarningSection $gap={6}>
                    <WarningSectionHeading>
                        ⚠ {step.warnings.length} quality {step.warnings.length === 1 ? 'warning' : 'warnings'}
                    </WarningSectionHeading>
                    {step.warnings.map((w, i) => (
                        <WarningEntry key={i} $gap={2}>
                            <WarningRuleTag>{formatWarningScope(w)}</WarningRuleTag>
                            <span>{w.message}</span>
                        </WarningEntry>
                    ))}
                </WarningSection>
            )}

            {hasData && fileReference && fileName && (
                <DetailSection>
                    <DataPreview fileReference={fileReference} fileName={fileName} signedUrl={signedUrl} />
                </DetailSection>
            )}

            {hasData && !fileReference && !isLatestRun && (
                <DetailSection>
                    <NoDataMessage>Data preview only available for the latest run</NoDataMessage>
                </DetailSection>
            )}

            {hasData && !fileReference && isLatestRun && hasNonPreviewableFile && (
                <DetailSection>
                    <NoDataMessage>Data preview not available for this format (json)</NoDataMessage>
                </DetailSection>
            )}

            {hasData && !fileReference && isLatestRun && !hasNonPreviewableFile && (
                <DetailSection>
                    <NoDataMessage>No data file found for this step</NoDataMessage>
                </DetailSection>
            )}
        </DetailContainer>
    );
}
